import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaEdit,
  FaTrash,
  FaSearch,
  FaUmbrellaBeach,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaIdCard,
  FaUser,
  FaCrown,
  FaStar,
  FaEye,
  FaBuilding,
  FaGlobe,
  FaAward,
  FaCheckCircle,
  FaTimesCircle,
  FaCamera,
  FaIdBadge,
  FaShieldAlt,
  FaMobileAlt,
  FaAt,
  FaUpload,
  FaImage,
  FaSpinner,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const LeisureGuestManagement = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [newGuest, setNewGuest] = useState({
    firstname: '',
    lastname: '',
    emailid: '',
    countrycode: '+91',
    phonenumber: '',
    clienttype: 'Leisure',
    country: 'India',
    preferredpaymentmethod: 'cash',
    password: '',
    aadhaar_verified: false,
  });
  const [editGuest, setEditGuest] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 25;
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [clientTypeFilter, setClientTypeFilter] = useState('');
  const [showFaceImageModal, setShowFaceImageModal] = useState(false);
  const [showAadhaarModal, setShowAadhaarModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageType, setImageType] = useState(null);
  const [uploadingFace, setUploadingFace] = useState(false);
  const [uploadingAadhaar, setUploadingAadhaar] = useState(false);
  const [uploadingGuestId, setUploadingGuestId] = useState(null);
  const navigate = useNavigate();

  // Fetch all guests with limit 300
  const fetchAllGuests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.guests) {
        setGuests(data.guests);
        setTotalCount(data.count || data.guests.length);
      } else {
        throw new Error('No guests data received');
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
      toast.error('Failed to load guests');
      setGuests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllGuests();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewGuest((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditGuest((prev) => ({ ...prev, [name]: value }));
  };

  // Upload face image to API
  const handleFaceImageUpload = async (guestId, file) => {
    setUploadingFace(true);
    setUploadingGuestId(guestId);

    const formData = new FormData();
    formData.append('guest_id', guestId);
    formData.append('file', file);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/update_profile`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload face image');
      }

      const data = await response.json();

      if (data.face_embeddings_updated) {
        await fetchAllGuests();

        if (selectedGuest && selectedGuest.guestid === guestId) {
          const updatedGuest = await fetchGuestDetails(guestId);
          setSelectedGuest(updatedGuest);
        }

        toast.success('Face image uploaded and verified successfully!');
      } else {
        toast.warning('Face image uploaded but embedding generation failed');
      }
    } catch (error) {
      console.error('Error uploading face image:', error);
      toast.error(error.message);
    } finally {
      setUploadingFace(false);
      setUploadingGuestId(null);
    }
  };

  // Upload Aadhaar card image to API
  const handleAadhaarUpload = async (guestId, file) => {
    setUploadingAadhaar(true);
    setUploadingGuestId(guestId);

    const formData = new FormData();
    formData.append('guest_id', guestId);
    formData.append('aadhar_file', file);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/update_profile`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload Aadhaar card');
      }

      const data = await response.json();

      if (data.aadhar_updated) {
        await fetchAllGuests();

        if (selectedGuest && selectedGuest.guestid === guestId) {
          const updatedGuest = await fetchGuestDetails(guestId);
          setSelectedGuest(updatedGuest);
        }

        toast.success('Aadhaar card uploaded and verified successfully!');
      } else {
        toast.warning('Aadhaar card uploaded but verification pending');
      }
    } catch (error) {
      console.error('Error uploading Aadhaar card:', error);
      toast.error(error.message);
    } finally {
      setUploadingAadhaar(false);
      setUploadingGuestId(null);
    }
  };

  // Fetch single guest details
  const fetchGuestDetails = async (guestId) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        return data.guest;
      }
      return null;
    } catch (error) {
      console.error('Error fetching guest details:', error);
      return null;
    }
  };

  // Create new guest
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(newGuest),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Guest created successfully!');
        fetchAllGuests();
        setShowAddModal(false);
        setNewGuest({
          firstname: '',
          lastname: '',
          emailid: '',
          countrycode: '+91',
          phonenumber: '',
          clienttype: 'Leisure',
          country: 'India',
          preferredpaymentmethod: 'cash',
          password: '',
          aadhaar_verified: false,
        });
      } else {
        throw new Error(data.detail || 'Failed to create guest');
      }
    } catch (error) {
      console.error('Error creating guest:', error);
      toast.error(`Failed to create guest: ${error.message}`);
    }
  };

  // Update guest
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${editGuest.guestid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(editGuest),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Guest updated successfully!');
        setGuests((prevGuests) =>
          prevGuests.map((guest) => (guest.guestid === editGuest.guestid ? data.guest : guest))
        );
        setShowEditModal(false);
      } else {
        throw new Error(data.detail || 'Failed to update guest');
      }
    } catch (error) {
      console.error('Error updating guest:', error);
      toast.error(`Failed to update guest: ${error.message}`);
    }
  };

  // View guest details
  const handleViewGuest = async (guestId) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSelectedGuest(data.guest);
        setShowViewModal(true);
      } else {
        throw new Error('Failed to fetch guest details');
      }
    } catch (error) {
      console.error('Error fetching guest details:', error);
      toast.error('Failed to load guest details');
    }
  };

  const handleEditClick = (guest) => {
    setEditGuest({ ...guest });
    setShowEditModal(true);
  };

  // Delete guest
  const handleDelete = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${itemToDelete.guestid}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Guest deleted successfully!');
        setGuests((prevGuests) =>
          prevGuests.filter((guest) => guest.guestid !== itemToDelete.guestid)
        );
      } else {
        throw new Error(data.detail || 'Failed to delete guest');
      }
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting guest:', error);
      toast.error(`Failed to delete guest: ${error.message}`);
      setShowDeleteModal(false);
    }
  };

  // Open image viewer
  const openImageViewer = (imageUrl, type) => {
    setSelectedImage(imageUrl);
    setImageType(type);
    if (type === 'face') {
      setShowFaceImageModal(true);
    } else {
      setShowAadhaarModal(true);
    }
  };

  // Filter guests based on search term and client type
  const filteredGuests = guests.filter((guest) => {
    const matchesSearch =
      guest.guestid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.emailid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.phonenumber?.toString().includes(searchTerm) ||
      guest.companyname?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesClientType = clientTypeFilter ? guest.clienttype === clientTypeFilter : true;

    return matchesSearch && matchesClientType;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredGuests.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredGuests.slice(indexOfFirstRow, indexOfLastRow);

  // Max 5 visible page buttons
  const MAX_VISIBLE_PAGES = 5;

  const getVisiblePages = () => {
    if (totalPages <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let start = currentPage - Math.floor(MAX_VISIBLE_PAGES / 2);
    let end = start + MAX_VISIBLE_PAGES - 1;

    if (start < 1) {
      start = 1;
      end = MAX_VISIBLE_PAGES;
    }
    if (end > totalPages) {
      end = totalPages;
      start = totalPages - MAX_VISIBLE_PAGES + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, clientTypeFilter]);

  // Clamp current page if the list shrinks
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredGuests.length / rowsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredGuests.length, rowsPerPage, currentPage]);

  // Get unique client types for filter
  const clientTypes = [...new Set(guests.map((g) => g.clienttype).filter(Boolean))];

  const getVerificationStatus = (guest) => {
    if (guest.verification_status?.is_fully_verified) {
      return {
        status: 'Fully Verified',
        color: 'bg-green-100 text-green-800 border border-green-300',
        icon: FaCheckCircle,
        level: 'full',
      };
    }
    if (guest.aadhaar_verified) {
      return {
        status: 'Aadhaar Verified',
        color: 'bg-blue-100 text-blue-800 border border-blue-300',
        icon: FaIdCard,
        level: 'aadhaar',
      };
    }
    if (guest.verification_status?.email_verified) {
      return {
        status: 'Email Verified',
        color: 'bg-purple-100 text-purple-800 border border-purple-300',
        icon: FaAt,
        level: 'email',
      };
    }
    if (guest.verification_status?.phone_verified) {
      return {
        status: 'Phone Verified',
        color: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
        icon: FaMobileAlt,
        level: 'phone',
      };
    }
    return {
      status: 'Pending',
      color: 'bg-gray-100 text-gray-800 border border-gray-300',
      icon: FaTimesCircle,
      level: 'pending',
    };
  };

  const formatPhoneNumber = (countrycode, phonenumber) => {
    if (!phonenumber) return 'N/A';
    return `${countrycode || ''} ${phonenumber}`;
  };

  const getGuestTypeDisplay = (clienttype) => {
    switch (clienttype) {
      case 'Leisure':
        return {
          label: 'LEISURE',
          color: 'bg-teal-100 text-teal-800 border border-teal-300',
          icon: FaUmbrellaBeach,
        };
      case 'Corporate':
        return {
          label: 'CORPORATE',
          color: 'bg-indigo-100 text-indigo-800 border border-indigo-300',
          icon: FaBuilding,
        };
      default:
        return {
          label: 'REGULAR',
          color: 'bg-gray-100 text-gray-800 border border-gray-300',
          icon: FaUser,
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const toggleAadhaarVerified = () => {
    setNewGuest((prev) => ({ ...prev, aadhaar_verified: !prev.aadhaar_verified }));
  };

  const toggleEditAadhaarVerified = () => {
    setEditGuest((prev) => ({ ...prev, aadhaar_verified: !prev.aadhaar_verified }));
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen w-full overflow-x-hidden">
      <header className="flex justify-between items-center mb-3 bg-white p-3 rounded-lg shadow-sm">
        <h1 className="text-black text-xl font-bold ml-2">Customer Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate-overview')}
            className="btn text-gray-600 hover:text-black"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/leisure')}
            className="btn text-black font-bold "
          >
            Guest Management
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate')}
            className="btn text-gray-600 hover:text-black"
          >
            Corporate Accounts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
            className="btn text-gray-600 hover:text-black"
          >
            Contracts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/credit-verification')}
            className="btn text-gray-600 hover:text-black"
          >
            Credit & Verification
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/settings')}
            className="btn text-gray-600 hover:text-black"
          >
            Settings
          </button>
        </div>
      </header>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col items-center justify-center text-center space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Guest Management</h1>
          <p className="text-gray-600 text-sm font-medium max-w-2xl">
            Manage all guests including leisure and corporate accounts
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Guests</p>
              <p className="text-2xl font-bold text-gray-900">{guests.length}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FaUser className="text-gray-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Fully Verified</p>
              <p className="text-2xl font-bold text-gray-900">
                {guests.filter((g) => g.verification_status?.is_fully_verified).length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FaShieldAlt className="text-gray-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">With Profile Photos</p>
              <p className="text-2xl font-bold text-gray-900">
                {guests.filter((g) => g.profile_photo).length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FaCamera className="text-gray-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Aadhaar Verified</p>
              <p className="text-2xl font-bold text-gray-900">
                {guests.filter((g) => g.aadhaar_verified).length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FaIdCard className="text-gray-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Search, Filter and Add Button */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-6 space-y-4 md:space-y-0">
        <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3 w-full md:w-auto">
          <div className="relative w-full md:w-80 lg:w-96">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Name, Email, Mobile or Company"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={clientTypeFilter}
            onChange={(e) => setClientTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white"
          >
            <option value="">All Client Types</option>
            {clientTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-3">
          <button
            className="bg-black text-white px-4 md:px-6 py-2.5 rounded-lg hover:bg-gray-800 transition duration-200 font-semibold flex items-center space-x-2"
            onClick={() => setShowAddModal(true)}
          >
            <FaUser className="text-sm" />
            <span>Add New Guest</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden w-full overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                Guest Info
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                Contact
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                Client Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                Verification Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                Face Image
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                Aadhaar Card
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center p-8">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
                    <span className="ml-2 text-gray-600">Loading guests...</span>
                  </div>
                </td>
              </tr>
            ) : currentRows.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center p-8 text-gray-500">
                  No guests found
                </td>
              </tr>
            ) : (
              currentRows.map((guest) => {
                const verification = getVerificationStatus(guest);
                const guestType = getGuestTypeDisplay(guest.clienttype);
                const IconComponent = guestType.icon;
                const VerificationIcon = verification.icon;
                const isUploading = uploadingGuestId === guest.guestid;

                return (
                  <tr key={guest.guestid} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="font-semibold text-gray-900">
                          {guest.firstname} {guest.lastname}
                        </div>
                        <div className="text-xs text-gray-500 font-mono mt-1">
                          ID: {guest.guestid?.substring(0, 8)}...
                        </div>
                        {guest.companyname && (
                          <div className="text-xs text-gray-600 mt-1">{guest.companyname}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="text-gray-700 flex items-center space-x-2">
                          <FaEnvelope className="text-gray-400" />
                          <span>{guest.emailid || 'N/A'}</span>
                        </div>
                        <div className="text-gray-700 flex items-center space-x-2 mt-1">
                          <FaPhone className="text-gray-400" />
                          <span>{formatPhoneNumber(guest.countrycode, guest.phonenumber)}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${guestType.color}`}
                      >
                        <IconComponent className="mr-1" />
                        <span>{guestType.label}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${verification.color}`}
                      >
                        <VerificationIcon className="mr-1" />
                        <span>{verification.status}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {guest.profile_photo ? (
                          <button
                            onClick={() => openImageViewer(guest.profile_photo, 'face')}
                            className="relative group"
                            disabled={isUploading}
                          >
                            <img
                              src={guest.profile_photo}
                              alt="Profile"
                              className="w-10 h-10 rounded-full border-2 border-gray-300 object-cover cursor-pointer hover:opacity-80 transition"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition">
                              <FaEye className="text-white text-xs" />
                            </div>
                          </button>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
                            <FaUser className="text-gray-400" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {guest.aadhar_image_url ? (
                          <button
                            onClick={() => openImageViewer(guest.aadhar_image_url, 'aadhaar')}
                            className="flex items-center space-x-1 px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 transition"
                            disabled={isUploading}
                          >
                            <FaIdCard className="text-gray-600" />
                            <span className="text-xs text-gray-700">View</span>
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Not uploaded</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          className="text-gray-600 hover:text-gray-800 transition duration-150 p-2 rounded hover:bg-gray-100"
                          onClick={() => handleViewGuest(guest.guestid)}
                          title="View Guest"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          className="text-gray-600 hover:text-gray-800 transition duration-150 p-2 rounded hover:bg-gray-100"
                          onClick={() => handleEditClick(guest)}
                          title="Edit Guest"
                        >
                          <FaEdit size={16} />
                        </button>
                        <button
                          className="text-gray-600 hover:text-gray-800 transition duration-150 p-2 rounded hover:bg-gray-100"
                          onClick={() => {
                            setItemToDelete(guest);
                            setShowDeleteModal(true);
                          }}
                          title="Delete Guest"
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
        <div className="text-sm text-gray-700">
          Showing {filteredGuests.length === 0 ? 0 : indexOfFirstRow + 1} to{' '}
          {Math.min(indexOfLastRow, filteredGuests.length)} of {filteredGuests.length} guests
          (Total: {totalCount})
        </div>

        <div className="flex flex-wrap gap-2 justify-center items-center">
          {/* First page button */}
          <button
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300 transition duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            title="First Page"
          >
            «
          </button>

          {/* Previous */}
          <button
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          {/* Visible page numbers (max 5) */}
          {visiblePages.map((pageNum) => (
            <button
              key={pageNum}
              className={`px-4 py-2 rounded-lg font-semibold transition duration-200 ${
                currentPage === pageNum
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
              }`}
              onClick={() => setCurrentPage(pageNum)}
            >
              {pageNum}
            </button>
          ))}

          {/* Next */}
          <button
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </button>

          {/* Last page button */}
          <button
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300 transition duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            title="Last Page"
          >
            »
          </button>
        </div>
      </div>

      {/* Face Image Modal */}
      {showFaceImageModal && selectedImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <FaCamera className="text-gray-600" />
                <span>Face Image</span>
              </h3>
              <button
                onClick={() => setShowFaceImageModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="p-6 flex justify-center">
              <img src={selectedImage} alt="Face" className="max-w-full max-h-[70vh] rounded-lg" />
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowFaceImageModal(false)}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aadhaar Card Modal */}
      {showAadhaarModal && selectedImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <FaIdCard className="text-gray-600" />
                <span>Aadhaar Card</span>
              </h3>
              <button
                onClick={() => setShowAadhaarModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="p-6 flex justify-center">
              <img
                src={selectedImage}
                alt="Aadhaar Card"
                className="max-w-full max-h-[70vh] rounded-lg"
              />
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowAadhaarModal(false)}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced View Guest Details Modal */}
      {showViewModal && selectedGuest && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-white p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {selectedGuest.profile_photo ? (
                      <img
                        src={selectedGuest.profile_photo}
                        alt="Profile"
                        className="w-16 h-16 rounded-full border-2 border-gray-300 object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
                        <FaUser className="text-xl text-gray-400" />
                      </div>
                    )}
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleFaceImageUpload(selectedGuest.guestid, file);
                          }
                        };
                        input.click();
                      }}
                      className="absolute -bottom-2 -right-2 bg-black text-white p-1.5 rounded-full hover:bg-gray-800 transition"
                      title="Update Face Image"
                    >
                      <FaCamera size={12} />
                    </button>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedGuest.firstname} {selectedGuest.lastname}
                    </h2>
                    <p className="text-gray-600 text-sm">
                      {selectedGuest.clienttype} Guest • Joined{' '}
                      {formatDate(selectedGuest.created_date)}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-300">
                        ID: {selectedGuest.guestid}
                      </span>
                      {selectedGuest.verification_status?.is_fully_verified && (
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-300 flex items-center space-x-1">
                          <FaShieldAlt className="text-xs" />
                          <span>Fully Verified</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold p-2 rounded-full hover:bg-gray-100 transition duration-200"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Left Column - Personal Information */}
                <div className="xl:col-span-2 space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <FaUser className="text-gray-600" />
                      <span>Personal Information</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Full Name</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {selectedGuest.firstname} {selectedGuest.lastname}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Email</span>
                          <span className="text-sm text-gray-900 flex items-center space-x-1">
                            <FaEnvelope className="text-gray-400" />
                            <span>{selectedGuest.emailid}</span>
                          </span>
                        </div>
                        {selectedGuest.companyname && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-200">
                            <span className="text-sm font-medium text-gray-600">Company</span>
                            <span className="text-sm text-gray-900">
                              {selectedGuest.companyname}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Phone</span>
                          <span className="text-sm font-semibold text-gray-900 flex items-center space-x-1">
                            <FaPhone className="text-gray-400" />
                            <span>
                              {formatPhoneNumber(
                                selectedGuest.countrycode,
                                selectedGuest.phonenumber
                              )}
                            </span>
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Country</span>
                          <span className="text-sm text-gray-900 flex items-center space-x-1">
                            <FaGlobe className="text-gray-400" />
                            <span>{selectedGuest.country || 'N/A'}</span>
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm font-medium text-gray-600">Client Type</span>
                          <span className="text-sm font-semibold text-gray-900 capitalize">
                            {selectedGuest.clienttype || 'leisure'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Aadhaar Card Section in View Modal */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <FaIdCard className="text-gray-600" />
                        <span>Aadhaar Card</span>
                      </h3>
                      <button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleAadhaarUpload(selectedGuest.guestid, file);
                            }
                          };
                          input.click();
                        }}
                        className="px-3 py-1.5 bg-black text-white text-sm rounded-lg hover:bg-gray-800 transition flex items-center space-x-2"
                      >
                        <FaUpload size={12} />
                        <span>Update Aadhaar</span>
                      </button>
                    </div>
                    {selectedGuest.aadhar_image_url ? (
                      <div className="text-center">
                        <img
                          src={selectedGuest.aadhar_image_url}
                          alt="Aadhaar Card"
                          className="max-w-full h-auto max-h-64 rounded border border-gray-300 mx-auto cursor-pointer"
                          onClick={() => openImageViewer(selectedGuest.aadhar_image_url, 'aadhaar')}
                        />
                        <p className="text-gray-600 text-sm mt-3">
                          Click on image to view full size
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-32 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg mx-auto flex items-center justify-center">
                          <FaIdCard className="text-4xl text-gray-400" />
                        </div>
                        <p className="text-gray-600 text-sm mt-3">No Aadhaar card uploaded</p>
                        <button
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleAadhaarUpload(selectedGuest.guestid, file);
                              }
                            };
                            input.click();
                          }}
                          className="mt-3 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition text-sm"
                        >
                          Upload Aadhaar Card
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Additional Details */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <FaIdBadge className="text-gray-600" />
                      <span>Additional Details</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">
                            Preferred Payment
                          </span>
                          <span className="text-sm text-gray-900 capitalize">
                            {selectedGuest.preferredpaymentmethod || 'cash'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Account Created</span>
                          <span className="text-sm text-gray-900">
                            {formatDate(selectedGuest.created_date)}
                          </span>
                        </div>
                        {selectedGuest.date_of_birth && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-200">
                            <span className="text-sm font-medium text-gray-600">Date of Birth</span>
                            <span className="text-sm text-gray-900">
                              {formatDate(selectedGuest.date_of_birth)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Last Updated</span>
                          <span className="text-sm text-gray-900">
                            {formatDate(selectedGuest.updated_date)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Profile Photo</span>
                          <span
                            className={`text-sm font-semibold ${selectedGuest.profile_photo ? 'text-gray-800' : 'text-gray-600'}`}
                          >
                            {selectedGuest.profile_photo ? 'Available' : 'Not Available'}
                          </span>
                        </div>
                        {selectedGuest.is_senior !== undefined && (
                          <div className="flex justify-between items-center py-2">
                            <span className="text-sm font-medium text-gray-600">
                              Senior Citizen
                            </span>
                            <span
                              className={`text-sm font-semibold ${selectedGuest.is_senior ? 'text-gray-800' : 'text-gray-600'}`}
                            >
                              {selectedGuest.is_senior ? 'Yes' : 'No'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Verification & Documents */}
                <div className="space-y-6">
                  {/* Verification Status */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <FaAward className="text-gray-600" />
                      <span>Verification Status</span>
                    </h3>
                    <div className="space-y-3">
                      <div
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          selectedGuest.verification_status?.is_fully_verified
                            ? 'bg-white border-green-300'
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-full ${
                              selectedGuest.verification_status?.is_fully_verified
                                ? 'bg-green-100 text-green-600'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            {selectedGuest.verification_status?.is_fully_verified ? (
                              <FaShieldAlt />
                            ) : (
                              <FaTimesCircle />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Full Verification</p>
                            <p className="text-xs text-gray-600">
                              {selectedGuest.verification_status?.is_fully_verified
                                ? 'Account fully verified'
                                : 'Account verification pending'}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            selectedGuest.verification_status?.is_fully_verified
                              ? 'bg-green-100 text-green-800 border border-green-300'
                              : 'bg-gray-100 text-gray-800 border border-gray-300'
                          }`}
                        >
                          {selectedGuest.verification_status?.is_fully_verified
                            ? 'VERIFIED'
                            : 'PENDING'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-full ${
                              selectedGuest.verification_status?.phone_verified
                                ? 'bg-green-100 text-green-600'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            <FaMobileAlt />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Phone</p>
                            <p className="text-xs text-gray-600">
                              {selectedGuest.verification_status?.phone_verified
                                ? 'Verified'
                                : 'Not verified'}
                            </p>
                          </div>
                        </div>
                        <FaCheckCircle
                          className={
                            selectedGuest.verification_status?.phone_verified
                              ? 'text-green-600'
                              : 'text-gray-300'
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-full ${
                              selectedGuest.verification_status?.email_verified
                                ? 'bg-green-100 text-green-600'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            <FaAt />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Email</p>
                            <p className="text-xs text-gray-600">
                              {selectedGuest.verification_status?.email_verified
                                ? 'Verified'
                                : 'Not verified'}
                            </p>
                          </div>
                        </div>
                        <FaCheckCircle
                          className={
                            selectedGuest.verification_status?.email_verified
                              ? 'text-green-600'
                              : 'text-gray-300'
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-full ${
                              selectedGuest.aadhaar_verified
                                ? 'bg-green-100 text-green-600'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            <FaIdCard />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Aadhaar</p>
                            <p className="text-xs text-gray-600">
                              {selectedGuest.aadhaar_verified ? 'Verified' : 'Not verified'}
                            </p>
                          </div>
                        </div>
                        <FaCheckCircle
                          className={
                            selectedGuest.aadhaar_verified ? 'text-green-600' : 'text-gray-300'
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Profile Photo Section */}
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <FaCamera className="text-gray-600" />
                        <span>Face Image</span>
                      </h3>
                      <button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleFaceImageUpload(selectedGuest.guestid, file);
                            }
                          };
                          input.click();
                        }}
                        className="px-3 py-1.5 bg-black text-white text-sm rounded-lg hover:bg-gray-800 transition flex items-center space-x-2"
                      >
                        <FaUpload size={12} />
                        <span>Update</span>
                      </button>
                    </div>
                    {selectedGuest.profile_photo ? (
                      <div className="text-center">
                        <img
                          src={selectedGuest.profile_photo}
                          alt="Profile"
                          className="w-40 h-40 rounded-full border-2 border-gray-300 mx-auto object-cover cursor-pointer"
                          onClick={() => openImageViewer(selectedGuest.profile_photo, 'face')}
                        />
                        <p className="text-gray-700 text-sm font-semibold mt-3 flex items-center justify-center space-x-1">
                          <FaCheckCircle className="text-green-600" />
                          <span>Face recognition enabled</span>
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="w-40 h-40 rounded-full bg-gray-100 border-2 border-gray-300 mx-auto flex items-center justify-center">
                          <FaUser className="text-4xl text-gray-400" />
                        </div>
                        <p className="text-gray-600 text-sm mt-3">No profile photo uploaded</p>
                        <button
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleFaceImageUpload(selectedGuest.guestid, file);
                              }
                            };
                            input.click();
                          }}
                          className="mt-3 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition text-sm"
                        >
                          Upload Face Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Last updated: {formatDate(selectedGuest.updated_date)}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleEditClick(selectedGuest);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition duration-200 font-semibold flex items-center space-x-2"
                  >
                    <FaEdit className="text-sm" />
                    <span>Edit Guest</span>
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
                  >
                    Close Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for adding new guest */}
      {showAddModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white p-6 rounded-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center space-x-3 mb-4">
              <FaUser className="text-xl text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Add New Guest</h2>
            </div>
            <p className="text-gray-600 mb-6 text-sm">Add a new guest to the system</p>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    name="firstname"
                    placeholder="Enter First Name"
                    value={newGuest.firstname}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="lastname"
                    placeholder="Enter Last Name"
                    value={newGuest.lastname}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="email"
                      name="emailid"
                      placeholder="Enter Email Address"
                      value={newGuest.emailid}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">Phone Number *</label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="tel"
                      name="phonenumber"
                      placeholder="Enter Phone Number"
                      value={newGuest.phonenumber}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">Client Type *</label>
                  <select
                    name="clienttype"
                    value={newGuest.clienttype}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    required
                  >
                    <option value="Leisure">Leisure</option>
                    <option value="Corporate">Corporate</option>
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">Country Code</label>
                  <input
                    type="text"
                    name="countrycode"
                    placeholder="+91"
                    value={newGuest.countrycode}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    name="country"
                    placeholder="Country"
                    value={newGuest.country}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">
                    Preferred Payment
                  </label>
                  <select
                    name="preferredpaymentmethod"
                    value={newGuest.preferredpaymentmethod}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="netbanking">Net Banking</option>
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Set Password"
                    value={newGuest.password}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  />
                </div>

                <div className="flex flex-col md:col-span-2">
                  <label className="text-sm font-semibold text-gray-700 mb-3">
                    Aadhaar Verification
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={toggleAadhaarVerified}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ${
                        newGuest.aadhaar_verified ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                          newGuest.aadhaar_verified ? 'translate-x-8' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span
                      className={`text-sm font-semibold ${
                        newGuest.aadhaar_verified ? 'text-gray-800' : 'text-gray-600'
                      }`}
                    >
                      {newGuest.aadhaar_verified ? 'Aadhaar Verified' : 'Aadhaar Not Verified'}
                    </span>
                    {newGuest.aadhaar_verified ? (
                      <FaCheckCircle className="text-gray-600 text-lg" />
                    ) : (
                      <FaTimesCircle className="text-gray-400 text-lg" />
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
                >
                  Add Guest
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for editing guest */}
      {showEditModal && editGuest && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white p-6 rounded-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center space-x-3 mb-4">
              <FaEdit className="text-xl text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Edit Guest</h2>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">Guest ID</label>
                  <input
                    type="text"
                    value={editGuest.guestid}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    disabled
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    name="firstname"
                    value={editGuest.firstname}
                    onChange={handleEditInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    name="lastname"
                    value={editGuest.lastname}
                    onChange={handleEditInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    required
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="email"
                      name="emailid"
                      value={editGuest.emailid}
                      onChange={handleEditInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="tel"
                      name="phonenumber"
                      value={editGuest.phonenumber}
                      onChange={handleEditInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">Client Type *</label>
                  <select
                    name="clienttype"
                    value={editGuest.clienttype}
                    onChange={handleEditInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    required
                  >
                    <option value="Leisure">Leisure</option>
                    <option value="Corporate">Corporate</option>
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">Country Code</label>
                  <input
                    type="text"
                    name="countrycode"
                    value={editGuest.countrycode}
                    onChange={handleEditInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={editGuest.country}
                    onChange={handleEditInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">
                    Preferred Payment
                  </label>
                  <select
                    name="preferredpaymentmethod"
                    value={editGuest.preferredpaymentmethod}
                    onChange={handleEditInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="netbanking">Net Banking</option>
                  </select>
                </div>

                {editGuest.clienttype === 'Corporate' && (
                  <>
                    <div className="flex flex-col">
                      <label className="text-sm font-semibold text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="companyname"
                        value={editGuest.companyname || ''}
                        onChange={handleEditInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-semibold text-gray-700 mb-2">Company ID</label>
                      <input
                        type="text"
                        name="companyid"
                        value={editGuest.companyid || ''}
                        onChange={handleEditInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-semibold text-gray-700 mb-2">
                        Employee ID
                      </label>
                      <input
                        type="text"
                        name="employeeid"
                        value={editGuest.employeeid || ''}
                        onChange={handleEditInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                  </>
                )}

                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-2">
                    Aadhaar Verified
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={toggleEditAadhaarVerified}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ${
                        editGuest.aadhaar_verified ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                          editGuest.aadhaar_verified ? 'translate-x-8' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span
                      className={`text-sm font-semibold ${
                        editGuest.aadhaar_verified ? 'text-gray-800' : 'text-gray-600'
                      }`}
                    >
                      {editGuest.aadhaar_verified ? 'Verified' : 'Not Verified'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
                >
                  Update Guest
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white p-6 rounded-xl border border-gray-200 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <FaTrash className="text-gray-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-900">Delete Guest</h2>
            </div>
            <p className="text-gray-700 mb-2">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-gray-900">
                {itemToDelete.firstname} {itemToDelete.lastname}
              </span>
              ?
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Guest ID: <span className="font-mono">{itemToDelete.guestid}</span>
            </p>
            <p className="text-sm text-gray-700 font-semibold mb-6">
              This action cannot be undone and will permanently remove all guest data.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-5 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
                onClick={handleDelete}
              >
                Delete Guest
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default LeisureGuestManagement;

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import {
//   FaEdit,
//   FaTrash,
//   FaSearch,
//   FaUmbrellaBeach,
//   FaPhone,
//   FaEnvelope,
//   FaMapMarkerAlt,
//   FaIdCard,
//   FaUser,
//   FaCrown,
//   FaStar,
//   FaEye,
//   FaBuilding,
//   FaGlobe,
//   FaAward,
//   FaCheckCircle,
//   FaTimesCircle,
//   FaCamera,
//   FaIdBadge,
//   FaShieldAlt,
//   FaMobileAlt,
//   FaAt,
//   FaUpload,
//   FaImage,
//   FaSpinner,
// } from 'react-icons/fa';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const LeisureGuestManagement = () => {
//   const [guests, setGuests] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [showAddModal, setShowAddModal] = useState(false);
//   const [showEditModal, setShowEditModal] = useState(false);
//   const [showViewModal, setShowViewModal] = useState(false);
//   const [selectedGuest, setSelectedGuest] = useState(null);
//   const [newGuest, setNewGuest] = useState({
//     firstname: '',
//     lastname: '',
//     emailid: '',
//     countrycode: '+91',
//     phonenumber: '',
//     clienttype: 'Leisure',
//     country: 'India',
//     preferredpaymentmethod: 'cash',
//     password: '',
//     aadhaar_verified: false,
//   });
//   const [editGuest, setEditGuest] = useState(null);
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [itemToDelete, setItemToDelete] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const rowsPerPage = 20;
//   const [searchTerm, setSearchTerm] = useState('');
//   const [totalCount, setTotalCount] = useState(0);
//   const [clientTypeFilter, setClientTypeFilter] = useState('');
//   const [showFaceImageModal, setShowFaceImageModal] = useState(false);
//   const [showAadhaarModal, setShowAadhaarModal] = useState(false);
//   const [selectedImage, setSelectedImage] = useState(null);
//   const [imageType, setImageType] = useState(null);
//   const [uploadingFace, setUploadingFace] = useState(false);
//   const [uploadingAadhaar, setUploadingAadhaar] = useState(false);
//   const [uploadingGuestId, setUploadingGuestId] = useState(null);
//   const navigate = useNavigate();

//   // Fetch all guests with limit 300
//   const fetchAllGuests = async () => {
//     try {
//       setLoading(true);
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/`);

//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }

//       const data = await response.json();

//       if (data.success && data.guests) {
//         setGuests(data.guests);
//         setTotalCount(data.count || data.guests.length);
//       } else {
//         throw new Error('No guests data received');
//       }
//     } catch (error) {
//       console.error('Error fetching guests:', error);
//       toast.error('Failed to load guests');
//       setGuests([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchAllGuests();
//   }, []);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setNewGuest((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleEditInputChange = (e) => {
//     const { name, value } = e.target;
//     setEditGuest((prev) => ({ ...prev, [name]: value }));
//   };

//   // Upload face image to API
//   const handleFaceImageUpload = async (guestId, file) => {
//     setUploadingFace(true);
//     setUploadingGuestId(guestId);

//     const formData = new FormData();
//     formData.append('guest_id', guestId);
//     formData.append('file', file);

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/update_profile`, {
//         method: 'PUT',
//         body: formData,
//       });

//       if (!response.ok) {
//         const error = await response.json();
//         throw new Error(error.detail || 'Failed to upload face image');
//       }

//       const data = await response.json();

//       if (data.face_embeddings_updated) {
//         // Refetch guest data to get updated profile photo URL
//         await fetchAllGuests();

//         // If view modal is open, update selected guest
//         if (selectedGuest && selectedGuest.guestid === guestId) {
//           const updatedGuest = await fetchGuestDetails(guestId);
//           setSelectedGuest(updatedGuest);
//         }

//         toast.success('Face image uploaded and verified successfully!');
//       } else {
//         toast.warning('Face image uploaded but embedding generation failed');
//       }
//     } catch (error) {
//       console.error('Error uploading face image:', error);
//       toast.error(error.message);
//     } finally {
//       setUploadingFace(false);
//       setUploadingGuestId(null);
//     }
//   };

//   // Upload Aadhaar card image to API
//   const handleAadhaarUpload = async (guestId, file) => {
//     setUploadingAadhaar(true);
//     setUploadingGuestId(guestId);

//     const formData = new FormData();
//     formData.append('guest_id', guestId);
//     formData.append('aadhar_file', file);

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/update_profile`, {
//         method: 'PUT',
//         body: formData,
//       });

//       if (!response.ok) {
//         const error = await response.json();
//         throw new Error(error.detail || 'Failed to upload Aadhaar card');
//       }

//       const data = await response.json();

//       if (data.aadhar_updated) {
//         // Refetch guest data to get updated Aadhaar URL
//         await fetchAllGuests();

//         // If view modal is open, update selected guest
//         if (selectedGuest && selectedGuest.guestid === guestId) {
//           const updatedGuest = await fetchGuestDetails(guestId);
//           setSelectedGuest(updatedGuest);
//         }

//         toast.success('Aadhaar card uploaded and verified successfully!');
//       } else {
//         toast.warning('Aadhaar card uploaded but verification pending');
//       }
//     } catch (error) {
//       console.error('Error uploading Aadhaar card:', error);
//       toast.error(error.message);
//     } finally {
//       setUploadingAadhaar(false);
//       setUploadingGuestId(null);
//     }
//   };

//   // Fetch single guest details
//   const fetchGuestDetails = async (guestId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`);
//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }
//       const data = await response.json();
//       if (data.success) {
//         return data.guest;
//       }
//       return null;
//     } catch (error) {
//       console.error('Error fetching guest details:', error);
//       return null;
//     }
//   };

//   // Create new guest
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify(newGuest),
//       });

//       const data = await response.json();

//       if (data.success) {
//         toast.success('Guest created successfully!');
//         fetchAllGuests();
//         setShowAddModal(false);
//         setNewGuest({
//           firstname: '',
//           lastname: '',
//           emailid: '',
//           countrycode: '+91',
//           phonenumber: '',
//           clienttype: 'Leisure',
//           country: 'India',
//           preferredpaymentmethod: 'cash',
//           password: '',
//           aadhaar_verified: false,
//         });
//       } else {
//         throw new Error(data.detail || 'Failed to create guest');
//       }
//     } catch (error) {
//       console.error('Error creating guest:', error);
//       toast.error(`Failed to create guest: ${error.message}`);
//     }
//   };

//   // Update guest
//   const handleEditSubmit = async (e) => {
//     e.preventDefault();

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${editGuest.guestid}`, {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify(editGuest),
//       });

//       const data = await response.json();

//       if (data.success) {
//         toast.success('Guest updated successfully!');
//         setGuests((prevGuests) =>
//           prevGuests.map((guest) => (guest.guestid === editGuest.guestid ? data.guest : guest))
//         );
//         setShowEditModal(false);
//       } else {
//         throw new Error(data.detail || 'Failed to update guest');
//       }
//     } catch (error) {
//       console.error('Error updating guest:', error);
//       toast.error(`Failed to update guest: ${error.message}`);
//     }
//   };

//   // View guest details
//   const handleViewGuest = async (guestId) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`);

//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }

//       const data = await response.json();

//       if (data.success) {
//         setSelectedGuest(data.guest);
//         setShowViewModal(true);
//       } else {
//         throw new Error('Failed to fetch guest details');
//       }
//     } catch (error) {
//       console.error('Error fetching guest details:', error);
//       toast.error('Failed to load guest details');
//     }
//   };

//   const handleEditClick = (guest) => {
//     setEditGuest({ ...guest });
//     setShowEditModal(true);
//   };

//   // Delete guest
//   const handleDelete = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${itemToDelete.guestid}`, {
//         method: 'DELETE',
//         headers: {
//           Accept: 'application/json',
//         },
//       });

//       const data = await response.json();

//       if (data.success) {
//         toast.success('Guest deleted successfully!');
//         setGuests((prevGuests) =>
//           prevGuests.filter((guest) => guest.guestid !== itemToDelete.guestid)
//         );
//       } else {
//         throw new Error(data.detail || 'Failed to delete guest');
//       }
//       setShowDeleteModal(false);
//     } catch (error) {
//       console.error('Error deleting guest:', error);
//       toast.error(`Failed to delete guest: ${error.message}`);
//       setShowDeleteModal(false);
//     }
//   };

//   // Open image viewer
//   const openImageViewer = (imageUrl, type) => {
//     setSelectedImage(imageUrl);
//     setImageType(type);
//     if (type === 'face') {
//       setShowFaceImageModal(true);
//     } else {
//       setShowAadhaarModal(true);
//     }
//   };

//   // Filter guests based on search term and client type
//   const filteredGuests = guests.filter((guest) => {
//     const matchesSearch =
//       guest.guestid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       guest.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       guest.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       guest.emailid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       guest.phonenumber?.toString().includes(searchTerm) ||
//       guest.companyname?.toLowerCase().includes(searchTerm.toLowerCase());

//     const matchesClientType = clientTypeFilter ? guest.clienttype === clientTypeFilter : true;

//     return matchesSearch && matchesClientType;
//   });

//   const indexOfLastRow = currentPage * rowsPerPage;
//   const indexOfFirstRow = indexOfLastRow - rowsPerPage;
//   const currentRows = filteredGuests.slice(indexOfFirstRow, indexOfLastRow);

//   // Get unique client types for filter
//   const clientTypes = [...new Set(guests.map((g) => g.clienttype).filter(Boolean))];

//   const getVerificationStatus = (guest) => {
//     if (guest.verification_status?.is_fully_verified) {
//       return {
//         status: 'Fully Verified',
//         color: 'bg-green-100 text-green-800 border border-green-300',
//         icon: FaCheckCircle,
//         level: 'full',
//       };
//     }
//     if (guest.aadhaar_verified) {
//       return {
//         status: 'Aadhaar Verified',
//         color: 'bg-blue-100 text-blue-800 border border-blue-300',
//         icon: FaIdCard,
//         level: 'aadhaar',
//       };
//     }
//     if (guest.verification_status?.email_verified) {
//       return {
//         status: 'Email Verified',
//         color: 'bg-purple-100 text-purple-800 border border-purple-300',
//         icon: FaAt,
//         level: 'email',
//       };
//     }
//     if (guest.verification_status?.phone_verified) {
//       return {
//         status: 'Phone Verified',
//         color: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
//         icon: FaMobileAlt,
//         level: 'phone',
//       };
//     }
//     return {
//       status: 'Pending',
//       color: 'bg-gray-100 text-gray-800 border border-gray-300',
//       icon: FaTimesCircle,
//       level: 'pending',
//     };
//   };

//   const formatPhoneNumber = (countrycode, phonenumber) => {
//     if (!phonenumber) return 'N/A';
//     return `${countrycode || ''} ${phonenumber}`;
//   };

//   const getGuestTypeDisplay = (clienttype) => {
//     switch (clienttype) {
//       case 'Leisure':
//         return {
//           label: 'LEISURE',
//           color: 'bg-teal-100 text-teal-800 border border-teal-300',
//           icon: FaUmbrellaBeach,
//         };
//       case 'Corporate':
//         return {
//           label: 'CORPORATE',
//           color: 'bg-indigo-100 text-indigo-800 border border-indigo-300',
//           icon: FaBuilding,
//         };
//       default:
//         return {
//           label: 'REGULAR',
//           color: 'bg-gray-100 text-gray-800 border border-gray-300',
//           icon: FaUser,
//         };
//     }
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return 'N/A';
//     return new Date(dateString).toLocaleDateString('en-IN', {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric',
//       hour: '2-digit',
//       minute: '2-digit',
//     });
//   };

//   const toggleAadhaarVerified = () => {
//     setNewGuest((prev) => ({ ...prev, aadhaar_verified: !prev.aadhaar_verified }));
//   };

//   const toggleEditAadhaarVerified = () => {
//     setEditGuest((prev) => ({ ...prev, aadhaar_verified: !prev.aadhaar_verified }));
//   };

//   return (
//     <div className="p-4 md:p-6 bg-gray-50 min-h-screen w-full overflow-x-hidden">
//       <header className="flex justify-between items-center mb-3 bg-white p-3 rounded-lg shadow-sm">
//         <h1 className="text-black text-xl font-bold ml-2">Customer Management</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/corporate-overview')}
//             className="btn text-gray-600 hover:text-black"
//           >
//             Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/leisure')}
//             className="btn text-black font-bold "
//           >
//             Guest Management
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/corporate')}
//             className="btn text-gray-600 hover:text-black"
//           >
//             Corporate Accounts
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
//             className="btn text-gray-600 hover:text-black"
//           >
//             Contracts
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/credit-verification')}
//             className="btn text-gray-600 hover:text-black"
//           >
//             Credit & Verification
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/settings')}
//             className="btn text-gray-600 hover:text-black"
//           >
//             Settings
//           </button>
//         </div>
//       </header>

//       {/* Header */}
//       <div className="mb-8">
//         <div className="flex flex-col items-center justify-center text-center space-y-2">
//           <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Guest Management</h1>
//           <p className="text-gray-600 text-sm font-medium max-w-2xl">
//             Manage all guests including leisure and corporate accounts
//           </p>
//         </div>
//       </div>

//       {/* Stats Overview */}
//       <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
//         <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm font-medium text-gray-600">Total Guests</p>
//               <p className="text-2xl font-bold text-gray-900">{guests.length}</p>
//             </div>
//             <div className="p-3 bg-gray-100 rounded-full">
//               <FaUser className="text-gray-600 text-xl" />
//             </div>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm font-medium text-gray-600">Fully Verified</p>
//               <p className="text-2xl font-bold text-gray-900">
//                 {guests.filter((g) => g.verification_status?.is_fully_verified).length}
//               </p>
//             </div>
//             <div className="p-3 bg-gray-100 rounded-full">
//               <FaShieldAlt className="text-gray-600 text-xl" />
//             </div>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm font-medium text-gray-600">With Profile Photos</p>
//               <p className="text-2xl font-bold text-gray-900">
//                 {guests.filter((g) => g.profile_photo).length}
//               </p>
//             </div>
//             <div className="p-3 bg-gray-100 rounded-full">
//               <FaCamera className="text-gray-600 text-xl" />
//             </div>
//           </div>
//         </div>
//         <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm font-medium text-gray-600">Aadhaar Verified</p>
//               <p className="text-2xl font-bold text-gray-900">
//                 {guests.filter((g) => g.aadhaar_verified).length}
//               </p>
//             </div>
//             <div className="p-3 bg-gray-100 rounded-full">
//               <FaIdCard className="text-gray-600 text-xl" />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Search, Filter and Add Button */}
//       <div className="flex flex-col md:flex-row items-center justify-between mb-6 space-y-4 md:space-y-0">
//         <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3 w-full md:w-auto">
//           <div className="relative w-full md:w-80 lg:w-96">
//             <FaSearch className="absolute left-3 top-3 text-gray-400" />
//             <input
//               type="text"
//               placeholder="Search by Name, Email, Mobile or Company"
//               className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//             />
//           </div>

//           <select
//             value={clientTypeFilter}
//             onChange={(e) => setClientTypeFilter(e.target.value)}
//             className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white"
//           >
//             <option value="">All Client Types</option>
//             {clientTypes.map((type) => (
//               <option key={type} value={type}>
//                 {type}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div className="flex items-center space-x-3">
//           <button
//             className="bg-black text-white px-4 md:px-6 py-2.5 rounded-lg hover:bg-gray-800 transition duration-200 font-semibold flex items-center space-x-2"
//             onClick={() => setShowAddModal(true)}
//           >
//             <FaUser className="text-sm" />
//             <span>Add New Guest</span>
//           </button>
//         </div>
//       </div>

//       {/* Table */}
//       <div className="bg-white border border-gray-200 rounded-lg overflow-hidden w-full overflow-x-auto">
//         <table className="min-w-full text-sm">
//           <thead className="bg-gray-50 border-b border-gray-200">
//             <tr>
//               <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
//                 Guest Info
//               </th>
//               <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
//                 Contact
//               </th>
//               <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
//                 Client Type
//               </th>
//               <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
//                 Verification Status
//               </th>
//               <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
//                 Face Image
//               </th>
//               <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
//                 Aadhaar Card
//               </th>
//               <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
//                 Actions
//               </th>
//             </tr>
//           </thead>
//           <tbody className="bg-white divide-y divide-gray-200">
//             {loading ? (
//               <tr>
//                 <td colSpan="7" className="text-center p-8">
//                   <div className="flex justify-center items-center">
//                     <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
//                     <span className="ml-2 text-gray-600">Loading guests...</span>
//                   </div>
//                 </td>
//               </tr>
//             ) : currentRows.length === 0 ? (
//               <tr>
//                 <td colSpan="7" className="text-center p-8 text-gray-500">
//                   No guests found
//                 </td>
//               </tr>
//             ) : (
//               currentRows.map((guest) => {
//                 const verification = getVerificationStatus(guest);
//                 const guestType = getGuestTypeDisplay(guest.clienttype);
//                 const IconComponent = guestType.icon;
//                 const VerificationIcon = verification.icon;
//                 const isUploading = uploadingGuestId === guest.guestid;

//                 return (
//                   <tr key={guest.guestid} className="hover:bg-gray-50 transition duration-150">
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <div>
//                         <div className="font-semibold text-gray-900">
//                           {guest.firstname} {guest.lastname}
//                         </div>
//                         <div className="text-xs text-gray-500 font-mono mt-1">
//                           ID: {guest.guestid?.substring(0, 8)}...
//                         </div>
//                         {guest.companyname && (
//                           <div className="text-xs text-gray-600 mt-1">{guest.companyname}</div>
//                         )}
//                       </div>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <div className="text-sm">
//                         <div className="text-gray-700 flex items-center space-x-2">
//                           <FaEnvelope className="text-gray-400" />
//                           <span>{guest.emailid || 'N/A'}</span>
//                         </div>
//                         <div className="text-gray-700 flex items-center space-x-2 mt-1">
//                           <FaPhone className="text-gray-400" />
//                           <span>{formatPhoneNumber(guest.countrycode, guest.phonenumber)}</span>
//                         </div>
//                       </div>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <span
//                         className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${guestType.color}`}
//                       >
//                         <IconComponent className="mr-1" />
//                         <span>{guestType.label}</span>
//                       </span>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <span
//                         className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${verification.color}`}
//                       >
//                         <VerificationIcon className="mr-1" />
//                         <span>{verification.status}</span>
//                       </span>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <div className="flex items-center space-x-2">
//                         {guest.profile_photo ? (
//                           <button
//                             onClick={() => openImageViewer(guest.profile_photo, 'face')}
//                             className="relative group"
//                             disabled={isUploading}
//                           >
//                             <img
//                               src={guest.profile_photo}
//                               alt="Profile"
//                               className="w-10 h-10 rounded-full border-2 border-gray-300 object-cover cursor-pointer hover:opacity-80 transition"
//                             />
//                             <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition">
//                               <FaEye className="text-white text-xs" />
//                             </div>
//                           </button>
//                         ) : (
//                           <div className="w-10 h-10 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
//                             <FaUser className="text-gray-400" />
//                           </div>
//                         )}
//                       </div>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <div className="flex items-center space-x-2">
//                         {guest.aadhar_image_url ? (
//                           <button
//                             onClick={() => openImageViewer(guest.aadhar_image_url, 'aadhaar')}
//                             className="flex items-center space-x-1 px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 transition"
//                             disabled={isUploading}
//                           >
//                             <FaIdCard className="text-gray-600" />
//                             <span className="text-xs text-gray-700">View</span>
//                           </button>
//                         ) : (
//                           <span className="text-xs text-gray-400">Not uploaded</span>
//                         )}
//                       </div>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
//                       <div className="flex space-x-2">
//                         <button
//                           className="text-gray-600 hover:text-gray-800 transition duration-150 p-2 rounded hover:bg-gray-100"
//                           onClick={() => handleViewGuest(guest.guestid)}
//                           title="View Guest"
//                         >
//                           <FaEye size={16} />
//                         </button>
//                         <button
//                           className="text-gray-600 hover:text-gray-800 transition duration-150 p-2 rounded hover:bg-gray-100"
//                           onClick={() => handleEditClick(guest)}
//                           title="Edit Guest"
//                         >
//                           <FaEdit size={16} />
//                         </button>
//                         <button
//                           className="text-gray-600 hover:text-gray-800 transition duration-150 p-2 rounded hover:bg-gray-100"
//                           onClick={() => {
//                             setItemToDelete(guest);
//                             setShowDeleteModal(true);
//                           }}
//                           title="Delete Guest"
//                         >
//                           <FaTrash size={16} />
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 );
//               })
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
//         <div className="text-sm text-gray-700">
//           Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredGuests.length)} of{' '}
//           {filteredGuests.length} guests (Total: {totalCount})
//         </div>
//         <div className="flex flex-wrap gap-2 justify-center">
//           <button
//             className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
//             onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
//             disabled={currentPage === 1}
//           >
//             Previous
//           </button>
//           {Array.from({ length: Math.ceil(filteredGuests.length / rowsPerPage) }, (_, i) => (
//             <button
//               key={i + 1}
//               className={`px-4 py-2 rounded-lg font-semibold transition duration-200 ${
//                 currentPage === i + 1
//                   ? 'bg-black text-white'
//                   : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
//               }`}
//               onClick={() => setCurrentPage(i + 1)}
//             >
//               {i + 1}
//             </button>
//           ))}
//           <button
//             className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
//             onClick={() =>
//               setCurrentPage((prev) =>
//                 Math.min(prev + 1, Math.ceil(filteredGuests.length / rowsPerPage))
//               )
//             }
//             disabled={currentPage === Math.ceil(filteredGuests.length / rowsPerPage)}
//           >
//             Next
//           </button>
//         </div>
//       </div>

//       {/* Face Image Modal */}
//       {showFaceImageModal && selectedImage && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50 p-4">
//           <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
//             <div className="flex justify-between items-center p-4 border-b border-gray-200">
//               <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
//                 <FaCamera className="text-gray-600" />
//                 <span>Face Image</span>
//               </h3>
//               <button
//                 onClick={() => setShowFaceImageModal(false)}
//                 className="text-gray-400 hover:text-gray-600 text-xl font-bold"
//               >
//                 ✕
//               </button>
//             </div>
//             <div className="p-6 flex justify-center">
//               <img src={selectedImage} alt="Face" className="max-w-full max-h-[70vh] rounded-lg" />
//             </div>
//             <div className="p-4 border-t border-gray-200 flex justify-end">
//               <button
//                 onClick={() => setShowFaceImageModal(false)}
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Aadhaar Card Modal */}
//       {showAadhaarModal && selectedImage && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50 p-4">
//           <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
//             <div className="flex justify-between items-center p-4 border-b border-gray-200">
//               <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
//                 <FaIdCard className="text-gray-600" />
//                 <span>Aadhaar Card</span>
//               </h3>
//               <button
//                 onClick={() => setShowAadhaarModal(false)}
//                 className="text-gray-400 hover:text-gray-600 text-xl font-bold"
//               >
//                 ✕
//               </button>
//             </div>
//             <div className="p-6 flex justify-center">
//               <img
//                 src={selectedImage}
//                 alt="Aadhaar Card"
//                 className="max-w-full max-h-[70vh] rounded-lg"
//               />
//             </div>
//             <div className="p-4 border-t border-gray-200 flex justify-end">
//               <button
//                 onClick={() => setShowAadhaarModal(false)}
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Enhanced View Guest Details Modal */}
//       {showViewModal && selectedGuest && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
//           <div className="bg-white rounded-xl border border-gray-200 w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
//             {/* Header */}
//             <div className="bg-white p-6 border-b border-gray-200">
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center space-x-4">
//                   <div className="relative">
//                     {selectedGuest.profile_photo ? (
//                       <img
//                         src={selectedGuest.profile_photo}
//                         alt="Profile"
//                         className="w-16 h-16 rounded-full border-2 border-gray-300 object-cover"
//                       />
//                     ) : (
//                       <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
//                         <FaUser className="text-xl text-gray-400" />
//                       </div>
//                     )}
//                     <button
//                       onClick={() => {
//                         const input = document.createElement('input');
//                         input.type = 'file';
//                         input.accept = 'image/*';
//                         input.onchange = (e) => {
//                           const file = e.target.files[0];
//                           if (file) {
//                             handleFaceImageUpload(selectedGuest.guestid, file);
//                           }
//                         };
//                         input.click();
//                       }}
//                       className="absolute -bottom-2 -right-2 bg-black text-white p-1.5 rounded-full hover:bg-gray-800 transition"
//                       title="Update Face Image"
//                     >
//                       <FaCamera size={12} />
//                     </button>
//                   </div>
//                   <div>
//                     <h2 className="text-xl font-bold text-gray-900">
//                       {selectedGuest.firstname} {selectedGuest.lastname}
//                     </h2>
//                     <p className="text-gray-600 text-sm">
//                       {selectedGuest.clienttype} Guest • Joined{' '}
//                       {formatDate(selectedGuest.created_date)}
//                     </p>
//                     <div className="flex items-center space-x-2 mt-1">
//                       <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-300">
//                         ID: {selectedGuest.guestid}
//                       </span>
//                       {selectedGuest.verification_status?.is_fully_verified && (
//                         <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-300 flex items-center space-x-1">
//                           <FaShieldAlt className="text-xs" />
//                           <span>Fully Verified</span>
//                         </span>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//                 <button
//                   onClick={() => setShowViewModal(false)}
//                   className="text-gray-400 hover:text-gray-600 text-xl font-bold p-2 rounded-full hover:bg-gray-100 transition duration-200"
//                 >
//                   ✕
//                 </button>
//               </div>
//             </div>

//             {/* Content */}
//             <div className="flex-1 overflow-y-auto p-6">
//               <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
//                 {/* Left Column - Personal Information */}
//                 <div className="xl:col-span-2 space-y-6">
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <FaUser className="text-gray-600" />
//                       <span>Personal Information</span>
//                     </h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <div className="space-y-3">
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">Full Name</span>
//                           <span className="text-sm font-semibold text-gray-900">
//                             {selectedGuest.firstname} {selectedGuest.lastname}
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">Email</span>
//                           <span className="text-sm text-gray-900 flex items-center space-x-1">
//                             <FaEnvelope className="text-gray-400" />
//                             <span>{selectedGuest.emailid}</span>
//                           </span>
//                         </div>
//                         {selectedGuest.companyname && (
//                           <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                             <span className="text-sm font-medium text-gray-600">Company</span>
//                             <span className="text-sm text-gray-900">
//                               {selectedGuest.companyname}
//                             </span>
//                           </div>
//                         )}
//                       </div>
//                       <div className="space-y-3">
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">Phone</span>
//                           <span className="text-sm font-semibold text-gray-900 flex items-center space-x-1">
//                             <FaPhone className="text-gray-400" />
//                             <span>
//                               {formatPhoneNumber(
//                                 selectedGuest.countrycode,
//                                 selectedGuest.phonenumber
//                               )}
//                             </span>
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">Country</span>
//                           <span className="text-sm text-gray-900 flex items-center space-x-1">
//                             <FaGlobe className="text-gray-400" />
//                             <span>{selectedGuest.country || 'N/A'}</span>
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2">
//                           <span className="text-sm font-medium text-gray-600">Client Type</span>
//                           <span className="text-sm font-semibold text-gray-900 capitalize">
//                             {selectedGuest.clienttype || 'leisure'}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Aadhaar Card Section in View Modal */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <div className="flex justify-between items-center mb-4">
//                       <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
//                         <FaIdCard className="text-gray-600" />
//                         <span>Aadhaar Card</span>
//                       </h3>
//                       <button
//                         onClick={() => {
//                           const input = document.createElement('input');
//                           input.type = 'file';
//                           input.accept = 'image/*';
//                           input.onchange = (e) => {
//                             const file = e.target.files[0];
//                             if (file) {
//                               handleAadhaarUpload(selectedGuest.guestid, file);
//                             }
//                           };
//                           input.click();
//                         }}
//                         className="px-3 py-1.5 bg-black text-white text-sm rounded-lg hover:bg-gray-800 transition flex items-center space-x-2"
//                       >
//                         <FaUpload size={12} />
//                         <span>Update Aadhaar</span>
//                       </button>
//                     </div>
//                     {selectedGuest.aadhar_image_url ? (
//                       <div className="text-center">
//                         <img
//                           src={selectedGuest.aadhar_image_url}
//                           alt="Aadhaar Card"
//                           className="max-w-full h-auto max-h-64 rounded border border-gray-300 mx-auto cursor-pointer"
//                           onClick={() => openImageViewer(selectedGuest.aadhar_image_url, 'aadhaar')}
//                         />
//                         <p className="text-gray-600 text-sm mt-3">
//                           Click on image to view full size
//                         </p>
//                       </div>
//                     ) : (
//                       <div className="text-center py-8">
//                         <div className="w-32 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg mx-auto flex items-center justify-center">
//                           <FaIdCard className="text-4xl text-gray-400" />
//                         </div>
//                         <p className="text-gray-600 text-sm mt-3">No Aadhaar card uploaded</p>
//                         <button
//                           onClick={() => {
//                             const input = document.createElement('input');
//                             input.type = 'file';
//                             input.accept = 'image/*';
//                             input.onchange = (e) => {
//                               const file = e.target.files[0];
//                               if (file) {
//                                 handleAadhaarUpload(selectedGuest.guestid, file);
//                               }
//                             };
//                             input.click();
//                           }}
//                           className="mt-3 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition text-sm"
//                         >
//                           Upload Aadhaar Card
//                         </button>
//                       </div>
//                     )}
//                   </div>

//                   {/* Additional Details */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <FaIdBadge className="text-gray-600" />
//                       <span>Additional Details</span>
//                     </h3>
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <div className="space-y-3">
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">
//                             Preferred Payment
//                           </span>
//                           <span className="text-sm text-gray-900 capitalize">
//                             {selectedGuest.preferredpaymentmethod || 'cash'}
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">Account Created</span>
//                           <span className="text-sm text-gray-900">
//                             {formatDate(selectedGuest.created_date)}
//                           </span>
//                         </div>
//                         {selectedGuest.date_of_birth && (
//                           <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                             <span className="text-sm font-medium text-gray-600">Date of Birth</span>
//                             <span className="text-sm text-gray-900">
//                               {formatDate(selectedGuest.date_of_birth)}
//                             </span>
//                           </div>
//                         )}
//                       </div>
//                       <div className="space-y-3">
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">Last Updated</span>
//                           <span className="text-sm text-gray-900">
//                             {formatDate(selectedGuest.updated_date)}
//                           </span>
//                         </div>
//                         <div className="flex justify-between items-center py-2 border-b border-gray-200">
//                           <span className="text-sm font-medium text-gray-600">Profile Photo</span>
//                           <span
//                             className={`text-sm font-semibold ${selectedGuest.profile_photo ? 'text-gray-800' : 'text-gray-600'}`}
//                           >
//                             {selectedGuest.profile_photo ? 'Available' : 'Not Available'}
//                           </span>
//                         </div>
//                         {selectedGuest.is_senior !== undefined && (
//                           <div className="flex justify-between items-center py-2">
//                             <span className="text-sm font-medium text-gray-600">
//                               Senior Citizen
//                             </span>
//                             <span
//                               className={`text-sm font-semibold ${selectedGuest.is_senior ? 'text-gray-800' : 'text-gray-600'}`}
//                             >
//                               {selectedGuest.is_senior ? 'Yes' : 'No'}
//                             </span>
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Right Column - Verification & Documents */}
//                 <div className="space-y-6">
//                   {/* Verification Status */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
//                       <FaAward className="text-gray-600" />
//                       <span>Verification Status</span>
//                     </h3>
//                     <div className="space-y-3">
//                       <div
//                         className={`flex items-center justify-between p-3 rounded-lg border ${
//                           selectedGuest.verification_status?.is_fully_verified
//                             ? 'bg-white border-green-300'
//                             : 'bg-white border-gray-300'
//                         }`}
//                       >
//                         <div className="flex items-center space-x-3">
//                           <div
//                             className={`p-2 rounded-full ${
//                               selectedGuest.verification_status?.is_fully_verified
//                                 ? 'bg-green-100 text-green-600'
//                                 : 'bg-gray-100 text-gray-400'
//                             }`}
//                           >
//                             {selectedGuest.verification_status?.is_fully_verified ? (
//                               <FaShieldAlt />
//                             ) : (
//                               <FaTimesCircle />
//                             )}
//                           </div>
//                           <div>
//                             <p className="font-semibold text-gray-900">Full Verification</p>
//                             <p className="text-xs text-gray-600">
//                               {selectedGuest.verification_status?.is_fully_verified
//                                 ? 'Account fully verified'
//                                 : 'Account verification pending'}
//                             </p>
//                           </div>
//                         </div>
//                         <span
//                           className={`px-2 py-1 rounded-full text-xs font-semibold ${
//                             selectedGuest.verification_status?.is_fully_verified
//                               ? 'bg-green-100 text-green-800 border border-green-300'
//                               : 'bg-gray-100 text-gray-800 border border-gray-300'
//                           }`}
//                         >
//                           {selectedGuest.verification_status?.is_fully_verified
//                             ? 'VERIFIED'
//                             : 'PENDING'}
//                         </span>
//                       </div>

//                       <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
//                         <div className="flex items-center space-x-3">
//                           <div
//                             className={`p-2 rounded-full ${
//                               selectedGuest.verification_status?.phone_verified
//                                 ? 'bg-green-100 text-green-600'
//                                 : 'bg-gray-100 text-gray-400'
//                             }`}
//                           >
//                             <FaMobileAlt />
//                           </div>
//                           <div>
//                             <p className="font-semibold text-gray-900">Phone</p>
//                             <p className="text-xs text-gray-600">
//                               {selectedGuest.verification_status?.phone_verified
//                                 ? 'Verified'
//                                 : 'Not verified'}
//                             </p>
//                           </div>
//                         </div>
//                         <FaCheckCircle
//                           className={
//                             selectedGuest.verification_status?.phone_verified
//                               ? 'text-green-600'
//                               : 'text-gray-300'
//                           }
//                         />
//                       </div>

//                       <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
//                         <div className="flex items-center space-x-3">
//                           <div
//                             className={`p-2 rounded-full ${
//                               selectedGuest.verification_status?.email_verified
//                                 ? 'bg-green-100 text-green-600'
//                                 : 'bg-gray-100 text-gray-400'
//                             }`}
//                           >
//                             <FaAt />
//                           </div>
//                           <div>
//                             <p className="font-semibold text-gray-900">Email</p>
//                             <p className="text-xs text-gray-600">
//                               {selectedGuest.verification_status?.email_verified
//                                 ? 'Verified'
//                                 : 'Not verified'}
//                             </p>
//                           </div>
//                         </div>
//                         <FaCheckCircle
//                           className={
//                             selectedGuest.verification_status?.email_verified
//                               ? 'text-green-600'
//                               : 'text-gray-300'
//                           }
//                         />
//                       </div>

//                       <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
//                         <div className="flex items-center space-x-3">
//                           <div
//                             className={`p-2 rounded-full ${
//                               selectedGuest.aadhaar_verified
//                                 ? 'bg-green-100 text-green-600'
//                                 : 'bg-gray-100 text-gray-400'
//                             }`}
//                           >
//                             <FaIdCard />
//                           </div>
//                           <div>
//                             <p className="font-semibold text-gray-900">Aadhaar</p>
//                             <p className="text-xs text-gray-600">
//                               {selectedGuest.aadhaar_verified ? 'Verified' : 'Not verified'}
//                             </p>
//                           </div>
//                         </div>
//                         <FaCheckCircle
//                           className={
//                             selectedGuest.aadhaar_verified ? 'text-green-600' : 'text-gray-300'
//                           }
//                         />
//                       </div>
//                     </div>
//                   </div>

//                   {/* Profile Photo Section */}
//                   <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
//                     <div className="flex justify-between items-center mb-4">
//                       <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
//                         <FaCamera className="text-gray-600" />
//                         <span>Face Image</span>
//                       </h3>
//                       <button
//                         onClick={() => {
//                           const input = document.createElement('input');
//                           input.type = 'file';
//                           input.accept = 'image/*';
//                           input.onchange = (e) => {
//                             const file = e.target.files[0];
//                             if (file) {
//                               handleFaceImageUpload(selectedGuest.guestid, file);
//                             }
//                           };
//                           input.click();
//                         }}
//                         className="px-3 py-1.5 bg-black text-white text-sm rounded-lg hover:bg-gray-800 transition flex items-center space-x-2"
//                       >
//                         <FaUpload size={12} />
//                         <span>Update</span>
//                       </button>
//                     </div>
//                     {selectedGuest.profile_photo ? (
//                       <div className="text-center">
//                         <img
//                           src={selectedGuest.profile_photo}
//                           alt="Profile"
//                           className="w-40 h-40 rounded-full border-2 border-gray-300 mx-auto object-cover cursor-pointer"
//                           onClick={() => openImageViewer(selectedGuest.profile_photo, 'face')}
//                         />
//                         <p className="text-gray-700 text-sm font-semibold mt-3 flex items-center justify-center space-x-1">
//                           <FaCheckCircle className="text-green-600" />
//                           <span>Face recognition enabled</span>
//                         </p>
//                       </div>
//                     ) : (
//                       <div className="text-center py-4">
//                         <div className="w-40 h-40 rounded-full bg-gray-100 border-2 border-gray-300 mx-auto flex items-center justify-center">
//                           <FaUser className="text-4xl text-gray-400" />
//                         </div>
//                         <p className="text-gray-600 text-sm mt-3">No profile photo uploaded</p>
//                         <button
//                           onClick={() => {
//                             const input = document.createElement('input');
//                             input.type = 'file';
//                             input.accept = 'image/*';
//                             input.onchange = (e) => {
//                               const file = e.target.files[0];
//                               if (file) {
//                                 handleFaceImageUpload(selectedGuest.guestid, file);
//                               }
//                             };
//                             input.click();
//                           }}
//                           className="mt-3 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition text-sm"
//                         >
//                           Upload Face Image
//                         </button>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Footer */}
//             <div className="border-t border-gray-200 p-6 bg-gray-50">
//               <div className="flex justify-between items-center">
//                 <div className="text-sm text-gray-600">
//                   Last updated: {formatDate(selectedGuest.updated_date)}
//                 </div>
//                 <div className="flex space-x-3">
//                   <button
//                     onClick={() => {
//                       setShowViewModal(false);
//                       handleEditClick(selectedGuest);
//                     }}
//                     className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition duration-200 font-semibold flex items-center space-x-2"
//                   >
//                     <FaEdit className="text-sm" />
//                     <span>Edit Guest</span>
//                   </button>
//                   <button
//                     onClick={() => setShowViewModal(false)}
//                     className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
//                   >
//                     Close Details
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Modal for adding new guest */}
//       {showAddModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
//           <div className="bg-white p-6 rounded-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
//             <div className="flex items-center space-x-3 mb-4">
//               <FaUser className="text-xl text-gray-600" />
//               <h2 className="text-xl font-semibold text-gray-900">Add New Guest</h2>
//             </div>
//             <p className="text-gray-600 mb-6 text-sm">Add a new guest to the system</p>

//             <form onSubmit={handleSubmit}>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">First Name *</label>
//                   <input
//                     type="text"
//                     name="firstname"
//                     placeholder="Enter First Name"
//                     value={newGuest.firstname}
//                     onChange={handleInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                     required
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">Last Name *</label>
//                   <input
//                     type="text"
//                     name="lastname"
//                     placeholder="Enter Last Name"
//                     value={newGuest.lastname}
//                     onChange={handleInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                     required
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">
//                     Email Address *
//                   </label>
//                   <div className="relative">
//                     <FaEnvelope className="absolute left-3 top-3.5 text-gray-400" />
//                     <input
//                       type="email"
//                       name="emailid"
//                       placeholder="Enter Email Address"
//                       value={newGuest.emailid}
//                       onChange={handleInputChange}
//                       className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white"
//                       required
//                     />
//                   </div>
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">Phone Number *</label>
//                   <div className="relative">
//                     <FaPhone className="absolute left-3 top-3.5 text-gray-400" />
//                     <input
//                       type="tel"
//                       name="phonenumber"
//                       placeholder="Enter Phone Number"
//                       value={newGuest.phonenumber}
//                       onChange={handleInputChange}
//                       className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white"
//                       required
//                     />
//                   </div>
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">Client Type *</label>
//                   <select
//                     name="clienttype"
//                     value={newGuest.clienttype}
//                     onChange={handleInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                     required
//                   >
//                     <option value="Leisure">Leisure</option>
//                     <option value="Corporate">Corporate</option>
//                   </select>
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">Country Code</label>
//                   <input
//                     type="text"
//                     name="countrycode"
//                     placeholder="+91"
//                     value={newGuest.countrycode}
//                     onChange={handleInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">Country</label>
//                   <input
//                     type="text"
//                     name="country"
//                     placeholder="Country"
//                     value={newGuest.country}
//                     onChange={handleInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">
//                     Preferred Payment
//                   </label>
//                   <select
//                     name="preferredpaymentmethod"
//                     value={newGuest.preferredpaymentmethod}
//                     onChange={handleInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                   >
//                     <option value="cash">Cash</option>
//                     <option value="card">Card</option>
//                     <option value="upi">UPI</option>
//                     <option value="netbanking">Net Banking</option>
//                   </select>
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-1">Password</label>
//                   <input
//                     type="password"
//                     name="password"
//                     placeholder="Set Password"
//                     value={newGuest.password}
//                     onChange={handleInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                   />
//                 </div>

//                 <div className="flex flex-col md:col-span-2">
//                   <label className="text-sm font-semibold text-gray-700 mb-3">
//                     Aadhaar Verification
//                   </label>
//                   <div className="flex items-center space-x-4">
//                     <button
//                       type="button"
//                       onClick={toggleAadhaarVerified}
//                       className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ${
//                         newGuest.aadhaar_verified ? 'bg-gray-600' : 'bg-gray-300'
//                       }`}
//                     >
//                       <span
//                         className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
//                           newGuest.aadhaar_verified ? 'translate-x-8' : 'translate-x-1'
//                         }`}
//                       />
//                     </button>
//                     <span
//                       className={`text-sm font-semibold ${
//                         newGuest.aadhaar_verified ? 'text-gray-800' : 'text-gray-600'
//                       }`}
//                     >
//                       {newGuest.aadhaar_verified ? 'Aadhaar Verified' : 'Aadhaar Not Verified'}
//                     </span>
//                     {newGuest.aadhaar_verified ? (
//                       <FaCheckCircle className="text-gray-600 text-lg" />
//                     ) : (
//                       <FaTimesCircle className="text-gray-400 text-lg" />
//                     )}
//                   </div>
//                 </div>
//               </div>

//               <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
//                 <button
//                   type="button"
//                   className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
//                   onClick={() => setShowAddModal(false)}
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
//                 >
//                   Add Guest
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* Modal for editing guest */}
//       {showEditModal && editGuest && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
//           <div className="bg-white p-6 rounded-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
//             <div className="flex items-center space-x-3 mb-4">
//               <FaEdit className="text-xl text-gray-600" />
//               <h2 className="text-xl font-semibold text-gray-900">Edit Guest</h2>
//             </div>

//             <form onSubmit={handleEditSubmit}>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">Guest ID</label>
//                   <input
//                     type="text"
//                     value={editGuest.guestid}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
//                     disabled
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">First Name *</label>
//                   <input
//                     type="text"
//                     name="firstname"
//                     value={editGuest.firstname}
//                     onChange={handleEditInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                     required
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
//                   <input
//                     type="text"
//                     name="lastname"
//                     value={editGuest.lastname}
//                     onChange={handleEditInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                     required
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">
//                     Email Address *
//                   </label>
//                   <div className="relative">
//                     <FaEnvelope className="absolute left-3 top-3.5 text-gray-400" />
//                     <input
//                       type="email"
//                       name="emailid"
//                       value={editGuest.emailid}
//                       onChange={handleEditInputChange}
//                       className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white"
//                       required
//                     />
//                   </div>
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
//                   <div className="relative">
//                     <FaPhone className="absolute left-3 top-3.5 text-gray-400" />
//                     <input
//                       type="tel"
//                       name="phonenumber"
//                       value={editGuest.phonenumber}
//                       onChange={handleEditInputChange}
//                       className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white"
//                       required
//                     />
//                   </div>
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">Client Type *</label>
//                   <select
//                     name="clienttype"
//                     value={editGuest.clienttype}
//                     onChange={handleEditInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                     required
//                   >
//                     <option value="Leisure">Leisure</option>
//                     <option value="Corporate">Corporate</option>
//                   </select>
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">Country Code</label>
//                   <input
//                     type="text"
//                     name="countrycode"
//                     value={editGuest.countrycode}
//                     onChange={handleEditInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">Country</label>
//                   <input
//                     type="text"
//                     name="country"
//                     value={editGuest.country}
//                     onChange={handleEditInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                   />
//                 </div>

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">
//                     Preferred Payment
//                   </label>
//                   <select
//                     name="preferredpaymentmethod"
//                     value={editGuest.preferredpaymentmethod}
//                     onChange={handleEditInputChange}
//                     className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                   >
//                     <option value="cash">Cash</option>
//                     <option value="card">Card</option>
//                     <option value="upi">UPI</option>
//                     <option value="netbanking">Net Banking</option>
//                   </select>
//                 </div>

//                 {editGuest.clienttype === 'Corporate' && (
//                   <>
//                     <div className="flex flex-col">
//                       <label className="text-sm font-semibold text-gray-700 mb-2">
//                         Company Name
//                       </label>
//                       <input
//                         type="text"
//                         name="companyname"
//                         value={editGuest.companyname || ''}
//                         onChange={handleEditInputChange}
//                         className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                       />
//                     </div>
//                     <div className="flex flex-col">
//                       <label className="text-sm font-semibold text-gray-700 mb-2">Company ID</label>
//                       <input
//                         type="text"
//                         name="companyid"
//                         value={editGuest.companyid || ''}
//                         onChange={handleEditInputChange}
//                         className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                       />
//                     </div>
//                     <div className="flex flex-col">
//                       <label className="text-sm font-semibold text-gray-700 mb-2">
//                         Employee ID
//                       </label>
//                       <input
//                         type="text"
//                         name="employeeid"
//                         value={editGuest.employeeid || ''}
//                         onChange={handleEditInputChange}
//                         className="w-full p-3 border border-gray-300 rounded-lg bg-white"
//                       />
//                     </div>
//                   </>
//                 )}

//                 <div className="flex flex-col">
//                   <label className="text-sm font-semibold text-gray-700 mb-2">
//                     Aadhaar Verified
//                   </label>
//                   <div className="flex items-center space-x-4">
//                     <button
//                       type="button"
//                       onClick={toggleEditAadhaarVerified}
//                       className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ${
//                         editGuest.aadhaar_verified ? 'bg-gray-600' : 'bg-gray-300'
//                       }`}
//                     >
//                       <span
//                         className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
//                           editGuest.aadhaar_verified ? 'translate-x-8' : 'translate-x-1'
//                         }`}
//                       />
//                     </button>
//                     <span
//                       className={`text-sm font-semibold ${
//                         editGuest.aadhaar_verified ? 'text-gray-800' : 'text-gray-600'
//                       }`}
//                     >
//                       {editGuest.aadhaar_verified ? 'Verified' : 'Not Verified'}
//                     </span>
//                   </div>
//                 </div>
//               </div>

//               <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
//                 <button
//                   type="button"
//                   className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
//                   onClick={() => setShowEditModal(false)}
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
//                 >
//                   Update Guest
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* Delete Confirmation Modal */}
//       {showDeleteModal && itemToDelete && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
//           <div className="bg-white p-6 rounded-xl border border-gray-200 max-w-md w-full">
//             <div className="flex items-center space-x-3 mb-4">
//               <FaTrash className="text-gray-600 text-xl" />
//               <h2 className="text-xl font-semibold text-gray-900">Delete Guest</h2>
//             </div>
//             <p className="text-gray-700 mb-2">
//               Are you sure you want to delete{' '}
//               <span className="font-semibold text-gray-900">
//                 {itemToDelete.firstname} {itemToDelete.lastname}
//               </span>
//               ?
//             </p>
//             <p className="text-sm text-gray-500 mb-4">
//               Guest ID: <span className="font-mono">{itemToDelete.guestid}</span>
//             </p>
//             <p className="text-sm text-gray-700 font-semibold mb-6">
//               This action cannot be undone and will permanently remove all guest data.
//             </p>
//             <div className="flex justify-end space-x-3">
//               <button
//                 type="button"
//                 className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
//                 onClick={() => setShowDeleteModal(false)}
//               >
//                 Cancel
//               </button>
//               <button
//                 type="button"
//                 className="px-5 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
//                 onClick={handleDelete}
//               >
//                 Delete Guest
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <ToastContainer
//         position="top-right"
//         autoClose={5000}
//         hideProgressBar={false}
//         newestOnTop={false}
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />
//     </div>
//   );
// };

// export default LeisureGuestManagement;
